import { IconName } from "@deligoo/ui/src/icons/build"
import dayjs, { Dayjs } from "dayjs"
import Cookies from "js-cookie"
import localForage from "localforage"
import { createContext, useContext } from "react"
import WKTParser from "terraformer-wkt-parser"

import { Client, Order, Payment, TeamStats } from "@/types"
import { fetchSignInClient } from "@/utils"

export const PRIORITY_COLOR_VALUES = { alert: 3, warning: 2, success: 1 } as const

export function truncate(str: string, length: number) {
  return str.length < length ? str : `${str.substring(0, length - 3)}...`
}

export function formatPhoneNumber(phoneNumber: string | null) {
  return String(phoneNumber).replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")
}

export function getDateLabel(date: string) {
  return dayjs().isSame(date, "day") ? "today" : dayjs(date).format("DD-MM-YYYY")
}

export function getDateFromTime(time: string) {
  const [h, m] = time.split(":")
  return dayjs().set("h", Number(h)).set("m", Number(m))
}

export function formatTime(timestamp: Dayjs | string | null | undefined) {
  if (!timestamp) return ""
  return dayjs(timestamp).format("HH:mm")
}

export function formatDelay(delay: number, prefix = "", suffix = "") {
  if (Number.isNaN(delay)) return ""
  const sign = delay > 0 ? "-" : delay < 0 ? "+" : ""
  return `${prefix}${sign}${Math.abs(delay)} min${suffix}`
}

const STATS_KEYS = [
  "active_drivers_count",
  "unsettled_payments_count",
  "today_orders_count",
  "today_delivery_orders_count",
  "load_value",
  "blocked_drivers_count",
  "late_drivers_count",
  "dynamic_buffer_real_value",
  "omitted_orders_count",
] as const
type StatsKey = keyof Pick<TeamStats, typeof STATS_KEYS[number]>
type StatsColor = "alert" | "warning" | "success"

export function getStatsColor(stats: TeamStats, value?: StatsKey): StatsColor | undefined {
  const {
    active_drivers_count,
    unsettled_payments_count,
    today_orders_count,
    blocked_drivers_count,
    late_drivers_count,
    dynamic_buffer_real_value,
    total_drivers_count,
    omitted_orders_count,
  } = stats

  switch (value) {
    case "active_drivers_count": {
      if (!total_drivers_count) return
      if (active_drivers_count > total_drivers_count) return "success"

      const diff = total_drivers_count - active_drivers_count
      return diff === 0 ? "success" : diff <= 2 ? "warning" : "alert"
    }
    case "unsettled_payments_count": {
      const count = unsettled_payments_count
      return count > 15 ? "alert" : count >= 10 ? "warning" : "success"
    }
    case "today_orders_count": {
      const count = today_orders_count
      return count > 60 ? "alert" : count >= 20 ? "warning" : "success"
    }
    case "today_delivery_orders_count": {
      return "success"
    }
    case "load_value": {
      return
    }
    case "blocked_drivers_count": {
      if (!total_drivers_count) return

      const ratio = blocked_drivers_count / total_drivers_count
      return ratio > 0.3 ? "alert" : ratio >= 0.2 ? "warning" : "success"
    }
    case "late_drivers_count": {
      if (!total_drivers_count) return

      const ratio = late_drivers_count / total_drivers_count
      return ratio > 0.3 ? "alert" : ratio >= 0.2 ? "warning" : "success"
    }
    case "dynamic_buffer_real_value": {
      if (dynamic_buffer_real_value > 60) return "alert"
      if (dynamic_buffer_real_value > 40) return "warning"
      return "success"
    }
    case "omitted_orders_count": {
      if (omitted_orders_count > 0) return "alert"
      return "success"
    }
    default: {
      return STATS_KEYS.reduce((teamColor: StatsColor, currentKey: StatsKey) => {
        const currentKeyColor = getStatsColor(stats, currentKey)
        if (!currentKeyColor) return teamColor

        if (PRIORITY_COLOR_VALUES[currentKeyColor] > PRIORITY_COLOR_VALUES[teamColor]) {
          return currentKeyColor
        }

        return teamColor
      }, "success")
    }
  }
}

// Return icon name related to given string
export function getIconFor(str: string): IconName {
  switch (str) {
    // Payment methods:
    case "blik_tpay":
    case "card":
    case "paid":
      return "credit-card"
    case "cash_on_delivery":
    case "cash":
      return "cash"

    // Order state:
    case "pending":
    case "assigned":
    case "unassigned":
    case "published":
    case "expired":
      return "status"
    case "started":
      return "started"
    // case "at_location":
    case "in_progress":
      return "at-location"
    case "finished":
    case "completed":
      return "done"
    case "canceled":
    case "failed":
      return "block"

    // Transport types:
    case "car":
      return "private-car"
    case "bicycle":
      return "bike"
    case "scooter":
      return "scooter"

    default:
      return str as IconName // TODO
  }
}

export function escapeRegex(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

export async function signInPOS(clientId: Client["id"]) {
  const {
    json: { data },
  } = await fetchSignInClient(clientId)

  Cookies.remove("pos_admin_auth_host")
  Cookies.remove("pos_admin_auth_token")

  Cookies.set("pos_admin_auth_host", data.pos_admin_auth_host, {
    sameSite: "lax",
    secure: true,
    domain: "deligoo.pl",
  })

  Cookies.set("pos_admin_auth_token", data.pos_admin_auth_token, {
    sameSite: "lax",
    secure: true,
    domain: "deligoo.pl",
  })

  window.open(data.redirect_url, "_blank")
}

export async function retrieveSavedToken() {
  const token = await localForage.getItem<string>("token")
  return token
}

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT
export const isProductionEnvironment = ENVIRONMENT === "production"

export const zIndexMapMarkers = {
  driver_tasks_routes: 2000,
  restaurants: 1500,
  online_drivers: 1000,
  offline_drivers: 500,
  active_orders: 0,
  finished_orders: -500,
  failed_orders: -1000,
  canceled_orders: -1500,
}

export function isTruthyOrZero(value: unknown) {
  return Boolean(value || value === 0)
}

export function getTrafficValueColor(value: TeamStats["traffic_value"] | undefined) {
  if (value === null || value === undefined) return "muted"

  return value <= 70 ? "success" : value <= 100 ? "warning" : "alert"
}

export function getUniqueValues<T>(array: T[]) {
  return Array.from(new Set(array))
}

/**
 * Type guard that enforces `value` to be of type `number`.
 *
 * @remarks
 *
 * Note that `number` apart from floating point numbers also includes `+Infinity`,
 * `-Infinity` and `NaN` symbolic values.
 */
export function isNumber(value: unknown): value is number {
  return typeof value === "number"
}

export function formatPrice(price?: number | string | null, options?: { subunit?: boolean }) {
  if (!isTruthyOrZero(price)) return ""
  return options?.subunit ? (Number(price) / 100).toFixed(2) : Number(price).toFixed(2)
}

export function formatDistance(distance: Order["distance"] | null) {
  return `${Number(distance ?? 0).toFixed(2)} km`
}

export function getWeatherIconName(weatherCondition: string, options?: { withColor?: boolean }) {
  let iconName: IconName = "mist"

  switch (weatherCondition) {
    case "Drizzle":
      iconName = "shower-rain"
      break
    case "Clear":
      iconName = "sunny"
      break
    case "Clouds":
      iconName = "scattered-clouds"
      break
    case "Thunderstorm":
    case "Rain":
    case "Snow":
      iconName = weatherCondition.toLowerCase() as IconName
      break
    default:
      iconName = "mist"
      break
  }

  if (options?.withColor) return `${iconName}-color` as IconName
  return iconName
}

export function getDatetimeSortValue(a?: string | null, b?: string | null) {
  if (!a) return -1
  if (!b) return 1

  return a < b ? 1 : a > b ? -1 : 0
}

export function sortPaymentsByDate(a: Payment, b: Payment) {
  return getDatetimeSortValue(a.created_at, b.created_at)
}

export function createGenericContext<T extends unknown>() {
  // Create a context with a generic parameter or undefined
  const genericContext = createContext<T | undefined>(undefined)

  // Check if the value provided to the context is defined or throw an error
  const useGenericContext = () => {
    const isContextDefined = useContext(genericContext)

    if (!isContextDefined) {
      throw new Error("useGenericContext must be used within a Provider")
    }

    return isContextDefined
  }

  return [useGenericContext, genericContext.Provider] as const
}

export function parseLineString(polygon: string) {
  const parsedData = WKTParser.parse(polygon) as GeoJSON.LineString
  return parsedData.coordinates.map(([lng, lat]) => [lat, lng]) as Array<[number, number]> // GeoJSON allows [number, number, number]
}
